import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Sparkle,
  Warning,
  Camera,
  Wrench,
  Coins,
  CalendarBlank,
  Ruler,
  Cube,
  Gavel,
} from "@phosphor-icons/react";
import { IconArmchair, IconLayout2 } from "@tabler/icons-react";
import { photoForRoom } from "../../data/imagery";
import type { ProjectGraph, GraphNode } from "../../types/ontology";
import { cls } from "../../lib/cls";
import { renderFloorPlanSvg } from "../../lib/floorplanSvg";
import { formatMoney } from "../../lib/i18n/format";

/* Apple-style modal showing one room in full context: dimensions,
 * intents/styles, SKUs (with vendor + lead + price), materials, rules
 * that apply, alerts, and milestones it blocks. Everything derives
 * from the live project graph — no literal strings. */

interface Props {
  graph: ProjectGraph;
  roomId: string;
  onClose: () => void;
  onRender: (roomId: string) => void;
}

function attr<T = unknown>(n: GraphNode | undefined, key: string): T | undefined {
  if (!n) return undefined;
  const v = n.attrs[key];
  return v as T | undefined;
}

export function RoomDetailSheet({ graph, roomId, onClose, onRender }: Props): JSX.Element | null {
  const room = useMemo(() => graph.nodes.find((n) => n.id === roomId && n.kind === "Room"), [graph, roomId]);
  const [tab, setTab] = useState<"items" | "rules" | "schedule" | "plan">("items");
  const planSvg = useMemo(() => renderFloorPlanSvg({ graph, roomId, width: 720, height: 420 }), [graph, roomId]);
  const floorPlanNode = useMemo(() => {
    const edge = graph.edges.find((edg) => edg.kind === "HAS_FLOORPLAN" && edg.source === roomId);
    if (!edge) return undefined;
    return graph.nodes.find((nd) => nd.id === edge.target && nd.kind === "FloorPlan");
  }, [graph, roomId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!room) return null;

  // Walk graph for everything bound to this room.
  const skuIds = graph.edges
    .filter((edg) => edg.kind === "IN_ROOM" && edg.target === room.id)
    .map((edg) => edg.source);
  const skus = graph.nodes.filter((n) => skuIds.includes(n.id) && n.kind === "SKU");
  const skuTotal = skus.reduce((s, n) => s + Number(attr<number>(n, "price") ?? 0), 0);
  const longLead = skus.filter((s) => Number(attr<number>(s, "leadDays") ?? 0) > 60);

  const materialIds = new Set(
    graph.edges
      .filter((edg) => edg.kind === "MADE_OF" && skuIds.includes(edg.source))
      .map((edg) => edg.target),
  );
  const materials = graph.nodes.filter((n) => materialIds.has(n.id) && n.kind === "Material");

  const rulesAffecting = graph.edges
    .filter((edg) => edg.kind === "RULE_RECOMMENDS" && skuIds.includes(edg.target))
    .map((edg) => graph.nodes.find((n) => n.id === edg.source && n.kind === "Rule"))
    .filter((x): x is GraphNode => Boolean(x));

  const blockedMilestones = graph.edges
    .filter((edg) => edg.kind === "BLOCKED_BY" && skuIds.includes(edg.target))
    .map((edg) => graph.nodes.find((n) => n.id === edg.source && n.kind === "Milestone"))
    .filter((x): x is GraphNode => Boolean(x));

  const budgetLines = graph.edges
    .filter((edg) => edg.kind === "FOR_ROOM" && edg.target === room.id)
    .map((edg) => graph.nodes.find((n) => n.id === edg.source && n.kind === "BudgetLine"))
    .filter((x): x is GraphNode => Boolean(x));

  const area = attr<number>(room, "area");
  const ceilingHt = attr<number>(room, "ceilingHt");

  return (
    <AnimatePresence>
      <motion.div
        key="rd-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-0 z-[80] bg-ink-950/35 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.aside
        key="rd-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`${room.label} detail`}
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.98 }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-y-6 right-6 left-6 sm:left-auto sm:w-[min(720px,94vw)] z-[81] panel overflow-hidden flex flex-col"
      >
        {/* Hero */}
        <div className="relative h-44 sm:h-56 overflow-hidden">
          <img
            src={photoForRoom(room.label)}
            alt={room.label}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-ink-950/30 to-transparent" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close room detail"
            className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-cream/90 grid place-items-center text-ink-950 hover:bg-cream transition"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-4 right-4 text-cream">
            <div className="text-[10.5px] uppercase tracking-wider opacity-80">{graph.projectName}</div>
            <h2 className="font-newyork text-3xl leading-tight">{room.label}</h2>
            <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
              {typeof area === "number" ? (
                <span className="pill bg-cream/90 text-ink-950 backdrop-blur-sm"><Ruler weight="duotone" className="w-3 h-3" /> {area} sq ft</span>
              ) : null}
              {typeof ceilingHt === "number" ? (
                <span className="pill bg-cream/90 text-ink-950 backdrop-blur-sm">{ceilingHt} ft H</span>
              ) : null}
              <span className="pill bg-cream/90 text-ink-950 backdrop-blur-sm"><IconArmchair size={12} stroke={1.8} /> {skus.length} items</span>
              {longLead.length > 0 ? (
                <span className="pill bg-foyr text-cream"><Warning weight="duotone" className="w-3 h-3" /> {longLead.length} long-lead</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 sm:px-6 pt-3 border-b border-border/60">
          <div className="inline-flex gap-1 p-1 bg-cream-dark rounded-lg">
            {([
              { id: "items", label: `Items · ${skus.length}` },
              { id: "rules", label: `Rules · ${rulesAffecting.length}` },
              { id: "schedule", label: `Schedule · ${blockedMilestones.length}` },
              { id: "plan",  label: floorPlanNode ? "Plan" : "Plan · none" },
            ] as const).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cls(
                  "px-3 py-1.5 rounded-md text-[12px] font-medium transition-all",
                  tab === t.id ? "bg-cream text-ink-950 shadow-sm" : "text-ink-600 hover:text-ink-950",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-4">
          {tab === "items" ? (
            <>
              <header className="flex items-center justify-between">
                <div className="eyebrow">SKUs in this room</div>
                <div className="text-[11px] text-ink-500 tabular">
                  Total <span className="font-medium text-ink-950">{formatMoney(skuTotal)}</span>
                </div>
              </header>
              <div className="grid gap-2">
                {skus.map((s) => {
                  const price = Number(attr<number>(s, "price") ?? 0);
                  const lead = Number(attr<number>(s, "leadDays") ?? 0);
                  const styleMatch = Number(attr<number>(s, "styleMatch") ?? 0);
                  return (
                    <div key={s.id} className="rounded-xl border border-border bg-cream-dark/40 p-3 flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-foyr/10 text-foyr-deep grid place-items-center shrink-0">
                        <Cube weight="duotone" className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-newyork text-[14px] font-semibold text-ink-950 leading-tight truncate">{s.label}</div>
                        <div className="mt-1 flex flex-wrap gap-1 text-[10.5px] text-ink-600">
                          <span className="pill bg-cream border border-border">{formatMoney(price)}</span>
                          <span className={cls("pill border", lead > 60 ? "bg-foyr/10 border-foyr/30 text-foyr-deep" : "bg-cream border-border")}>
                            {lead}d lead
                          </span>
                          {styleMatch > 0 ? (
                            <span className="pill bg-cream border border-border">style {(styleMatch * 100).toFixed(0)}%</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {skus.length === 0 ? (
                  <div className="text-ink-500 text-[12px]">No items bound to this room yet.</div>
                ) : null}
              </div>

              {materials.length > 0 ? (
                <section>
                  <div className="eyebrow mb-2">Materials referenced</div>
                  <div className="flex flex-wrap gap-1.5">
                    {materials.map((m) => (
                      <span key={m.id} className="pill bg-cream border border-border text-ink-700">
                        <Wrench weight="duotone" className="w-3 h-3" /> {m.label}
                      </span>
                    ))}
                  </div>
                </section>
              ) : null}

              {budgetLines.length > 0 ? (
                <section>
                  <div className="eyebrow mb-2">Budget lines</div>
                  <div className="grid gap-1.5">
                    {budgetLines.map((b) => (
                      <div key={b.id} className="flex items-center justify-between text-[12px] rounded-lg border border-border bg-cream-dark/40 px-3 py-2">
                        <span className="inline-flex items-center gap-1.5 text-ink-700">
                          <Coins weight="duotone" className="w-4 h-4 text-foyr-deep" /> {b.label}
                        </span>
                        <span className="tabular text-ink-950">
                          {formatMoney(Number(attr<number>(b, "amount") ?? 0))}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : null}

          {tab === "rules" ? (
            <>
              <header className="eyebrow">Rules touching items in this room</header>
              <div className="grid gap-2">
                {rulesAffecting.map((r) => (
                  <div key={r.id} className="rounded-xl border border-border bg-cream-dark/40 p-3 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-foyr/10 text-foyr-deep grid place-items-center shrink-0">
                      <Gavel weight="duotone" className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-newyork text-[14px] font-semibold text-ink-950 leading-tight">{r.label}</div>
                      <div className="text-[11px] text-ink-600 mt-0.5 font-mono">
                        {String(attr<string>(r, "when") ?? "")}
                      </div>
                    </div>
                  </div>
                ))}
                {rulesAffecting.length === 0 ? (
                  <div className="text-ink-500 text-[12px]">No rule has fired against this room's items.</div>
                ) : null}
              </div>
            </>
          ) : null}

          {tab === "schedule" ? (
            <>
              <header className="eyebrow">Milestones blocked by items in this room</header>
              <div className="grid gap-2">
                {blockedMilestones.map((m) => (
                  <div key={m.id} className="rounded-xl border border-border bg-cream-dark/40 p-3 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-foyr/10 text-foyr-deep grid place-items-center shrink-0">
                      <CalendarBlank weight="duotone" className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-newyork text-[14px] font-semibold text-ink-950 leading-tight">{m.label}</div>
                      <div className="text-[11px] text-ink-600 mt-0.5">
                        due {String(attr<string>(m, "due") ?? "—")} · slack {String(attr<number>(m, "slackDays") ?? "—")}d
                      </div>
                    </div>
                  </div>
                ))}
                {blockedMilestones.length === 0 ? (
                  <div className="text-ink-500 text-[12px]">No critical-path milestones blocked by this room.</div>
                ) : null}
              </div>
            </>
          ) : null}

          {tab === "plan" ? (
            <>
              <header className="flex items-center justify-between">
                <div className="eyebrow inline-flex items-center gap-1.5">
                  <IconLayout2 size={12} stroke={1.8} /> Floor plan · derived from graph
                </div>
                {floorPlanNode ? (
                  <div className="text-[10.5px] text-ink-500 font-mono tabular">
                    {Number(attr<number>(floorPlanNode, "widthFt") ?? 0).toFixed(1)} × {Number(attr<number>(floorPlanNode, "depthFt") ?? 0).toFixed(1)} ft
                  </div>
                ) : null}
              </header>
              {floorPlanNode && planSvg ? (
                <>
                  <div
                    className="rounded-xl border border-border bg-cream-dark/20 overflow-hidden"
                    role="img"
                    aria-label={`Floor plan for ${room.label}`}
                    dangerouslySetInnerHTML={{ __html: planSvg }}
                  />
                  <div className="text-[10.5px] text-ink-500 leading-relaxed">
                    <div>
                      <span className="font-mono text-ink-700">{String(attr<string>(floorPlanNode, "planId") ?? "—")}</span>
                      {" · "}
                      {String(attr<string>(floorPlanNode, "planSource") ?? "")}
                    </div>
                    <div className="mt-1">
                      Renderer reads <code className="font-mono">FloorPlan.widthFt / depthFt / doorOpenings / windowOpenings</code> from the typed graph node. On real ingest these are populated by MinerU / ezdxf parsing CubiCasa5K vectors — same code path.
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-ink-500 text-[12px] border border-dashed border-border rounded-lg p-4 text-center">
                  No FloorPlan node attached to this Room. Attach one via the MinerU ingest pipeline, or via the Onboard flow when a vector plan is dropped.
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t border-border/60 bg-cream-dark/30 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="text-[11px] text-ink-500">
            <Sparkle weight="duotone" className="inline-block w-3 h-3 mr-1 text-foyr" />
            Derived live from project graph
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-outline !text-xs !py-1.5">Close</button>
            <button
              onClick={() => onRender(room.id)}
              className="btn btn-primary !text-xs !py-1.5 inline-flex items-center gap-1.5"
            >
              <Camera weight="duotone" className="w-4 h-4" /> Render this room
            </button>
          </div>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}
